import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

/**
 * Fila arrastrable de una columna: asa de arrastre, casilla de visibilidad y
 * botones subir/bajar (alternativa accesible al arrastre, útil en móvil).
 */
const ColumnRow = ({ column, index, total, onToggle, onMove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key })

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
    zIndex: isDragging ? 2 : undefined,
  }

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`cs-row ${isDragging ? 'is-dragging' : ''}`}
    >
      <button
        type="button"
        className="cs-handle"
        title="Arrastrar para reordenar"
        aria-label={`Reordenar columna ${column.label}`}
        {...attributes}
        {...listeners}
      >
        <i className="fa-solid fa-grip-vertical"></i>
      </button>

      <label className="cs-label">
        <input
          type="checkbox"
          className="form-check-input m-0"
          checked={column.visible}
          disabled={column.required}
          onChange={() => onToggle(column.key)}
        />
        <span>{column.label}</span>
        {column.required && <small className="cs-fixed">fija</small>}
      </label>

      <div className="cs-arrows">
        <button
          type="button"
          className="cs-arrow"
          disabled={index === 0}
          onClick={() => onMove(index, index - 1)}
          aria-label={`Subir columna ${column.label}`}
        >
          <i className="fa-solid fa-chevron-up"></i>
        </button>
        <button
          type="button"
          className="cs-arrow"
          disabled={index === total - 1}
          onClick={() => onMove(index, index + 1)}
          aria-label={`Bajar columna ${column.label}`}
        >
          <i className="fa-solid fa-chevron-down"></i>
        </button>
      </div>
    </li>
  )
}

/**
 * Panel para personalizar las columnas de una tabla: mostrar/ocultar y
 * reordenar (arrastrando o con las flechas). Es presentacional: el estado vive
 * en `useColumnLayout`.
 *
 * @component
 * @param {Object} props
 * @param {Array<{key: string, label: string, visible: boolean, required?: boolean}>} props.columns
 *   Columnas en su orden actual.
 * @param {(key: string) => void} props.onToggle - Muestra/oculta una columna.
 * @param {(from: number, to: number) => void} props.onMove - Mueve una columna.
 * @param {() => void} props.onReset - Restaura el layout por defecto.
 * @returns {JSX.Element}
 */
const ColumnSettings = ({ columns, onToggle, onMove, onReset }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return
    const from = columns.findIndex((col) => col.key === active.id)
    const to = columns.findIndex((col) => col.key === over.id)
    onMove(from, to)
  }

  return (
    <div className="column-settings">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span className="cs-title">Columnas visibles y orden</span>
        <button type="button" className="cs-reset" onClick={onReset}>
          <i className="fa-solid fa-rotate-left me-1"></i>Restablecer
        </button>
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={columns.map((col) => col.key)}
          strategy={verticalListSortingStrategy}
        >
          <ul className="cs-list">
            {columns.map((column, index) => (
              <ColumnRow
                key={column.key}
                column={column}
                index={index}
                total={columns.length}
                onToggle={onToggle}
                onMove={onMove}
              />
            ))}
          </ul>
        </SortableContext>
      </DndContext>

      <style>{`
        .cs-title { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; color: var(--gm-text-muted); }
        .cs-reset { border: none; background: transparent; color: var(--gm-primary); font-size: 0.8rem; font-weight: 600; }
        .cs-list { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 6px; }
        .cs-row {
          position: relative; display: flex; align-items: center; gap: 8px;
          padding: 6px 8px; border: 1px solid var(--gm-border); border-radius: 8px;
          background: var(--gm-surface);
        }
        .cs-row.is-dragging { box-shadow: var(--gm-shadow); border-color: var(--gm-primary); }
        .cs-handle { border: none; background: transparent; color: var(--gm-text-faint); cursor: grab; padding: 0 2px; touch-action: none; }
        .cs-handle:active { cursor: grabbing; }
        .cs-label { display: flex; align-items: center; gap: 8px; flex: 1; margin: 0; font-size: 0.85rem; color: var(--gm-text); cursor: pointer; }
        .cs-fixed { font-size: 0.65rem; color: var(--gm-text-faint); text-transform: uppercase; }
        .cs-arrows { display: flex; gap: 2px; }
        .cs-arrow { border: none; background: transparent; color: var(--gm-text-muted); width: 24px; height: 24px; border-radius: 6px; font-size: 0.7rem; }
        .cs-arrow:hover:not(:disabled) { background: var(--gm-surface-2); color: var(--gm-text); }
        .cs-arrow:disabled { opacity: 0.3; }
      `}</style>
    </div>
  )
}

export default ColumnSettings
